import {
  collection, doc, getDoc, setDoc, deleteDoc,
  query, orderBy, limit, getDocs, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

const COL = 'monthlyPnL';

// Derive qty sold (closing stock) from stock movement for the period:
//   Opening Stock + In Between Stock - Return Goods = Closing Stock (Sold Goods)
export const computeClosingStock = ({ openingStock, inBetweenStock, returnGoods }) => {
  const opening = parseFloat(openingStock) || 0;
  const inBetween = parseFloat(inBetweenStock) || 0;
  const returns = parseFloat(returnGoods) || 0;
  return opening + inBetween - returns;
};

// Compute a single product line from a product record + its stock movement for the period.
// qtySold (= Closing Stock / Sold Goods) is derived, never typed in directly:
//   Retail Total Value = Retail Price (sellingPrice) x Sold Goods
//   Total Cost Value   = Cost Price (costPrice) x Sold Goods
export const computeProductLine = ({ product, openingStock, inBetweenStock, returnGoods }) => {
  const opening = parseFloat(openingStock) || 0;
  const inBetween = parseFloat(inBetweenStock) || 0;
  const returns = parseFloat(returnGoods) || 0;
  const qtySold = computeClosingStock({ openingStock: opening, inBetweenStock: inBetween, returnGoods: returns });
  const costPrice = parseFloat(product.costPrice) || 0;
  const sellingPrice = parseFloat(product.sellingPrice) || 0;
  const lineCost = qtySold * costPrice; // Total Cost Value
  const lineRevenue = qtySold * sellingPrice; // Retail Total Value
  const marginPct = lineRevenue > 0 ? ((lineRevenue - lineCost) / lineRevenue) * 100 : 0;

  return {
    productId: product.id,
    name: product.name,
    nameTa: product.nameTa || '',
    category: product.category || '',
    openingStock: opening,
    inBetweenStock: inBetween,
    returnGoods: returns,
    qtySold, // Closing Stock / Sold Goods
    costPrice,
    sellingPrice,
    lineCost,
    lineRevenue,
    marginPct,
  };
};

// Compute the roll-up totals for the month from revenue + product lines + expenses
export const computeTotals = ({ revenue, productLines, expensesTotal }) => {
  const rev = parseFloat(revenue) || 0;
  const expenses = parseFloat(expensesTotal) || 0;
  const totalCOGS = productLines.reduce((s, l) => s + (l.lineCost || 0), 0);
  const totalProductRevenue = productLines.reduce((s, l) => s + (l.lineRevenue || 0), 0);
  const grossProfit = rev - totalCOGS;
  const netProfit = grossProfit - expenses;

  return { totalCOGS, totalProductRevenue, grossProfit, netProfit };
};

export const getMonthlyPnL = async (monthId) => {
  try {
    const snap = await getDoc(doc(db, COL, monthId));
    if (!snap.exists()) return { success: false };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

export const subscribeToMonthlyPnL = (monthId, callback) => {
  return onSnapshot(doc(db, COL, monthId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

export const saveMonthlyPnL = async (monthId, data) => {
  try {
    const existing = await getDoc(doc(db, COL, monthId));
    await setDoc(doc(db, COL, monthId), {
      ...data,
      month: monthId,
      createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: false });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

export const listMonthlyPnLHistory = async (limitCount = 12) => {
  try {
    const q = query(collection(db, COL), orderBy('month', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map((d) => ({ id: d.id, ...d.data() })) };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

export const deleteMonthlyPnL = async (monthId) => {
  try {
    await deleteDoc(doc(db, COL, monthId));
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};
